import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getESignContacts, saveESignContacts } from '@/api/deal.api';
import { Button } from '@/components/UI/Button';
import { H3 } from '@/components/UI/Typography/Heading';
import { Text } from '@/components/UI/Typography/Text';
import type { DealESignContactStatus } from '@/types/dealESign';
import { errorHandler } from '@/utils/toastHandler';

import { Actions, ContactList, ContactRow, ExistingContact, ModalSurface, ScrollArea } from './styles';

type Props = {
  open: boolean;
  dealId: string;
  onClose: () => void;
  onReady: () => Promise<void> | void;
};

const ESignContactModal = ({ open, dealId, onClose, onReady }: Props) => {
  // @ts-ignore The legacy locale catalogue exceeds TypeScript's inference depth.
  const { t } = useTranslation();
  const [status, setStatus] = useState<DealESignContactStatus | null>(null);
  const [emails, setEmails] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getESignContacts(dealId);
      setStatus(response.data.data);
      setEmails({});
    } catch {
      errorHandler(t('pages.dealDetail.manageFiles.files.contractSigning.contacts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [dealId, t]);

  useEffect(() => {
    if (open) load();
  }, [load, open]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!status) return;

    const missing = status.participants.filter((participant) => participant.missing);
    const contacts = missing.map((participant) => ({
      user_id: participant.user_id,
      email: (emails[participant.user_id] || '').trim(),
    }));
    if (contacts.some((contact) => !contact.email)) {
      errorHandler(t('pages.dealDetail.manageFiles.files.contractSigning.contacts.allRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await saveESignContacts(dealId, contacts);
      setStatus(response.data.data);
      if (response.data.data.ready) {
        onClose();
        await onReady();
      }
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message || t('pages.dealDetail.manageFiles.files.contractSigning.contacts.saveFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} aria-labelledby="esign-contact-title">
      <ModalSurface>
        <ScrollArea>
          <H3 id="esign-contact-title">
            {t('pages.dealDetail.manageFiles.files.contractSigning.contacts.title')}
          </H3>
          <Text marginTop="12px">
            {t('pages.dealDetail.manageFiles.files.contractSigning.contacts.description')}
          </Text>

          <form onSubmit={submit}>
            <ContactList>
              {status?.participants.map((participant) => (
                <ContactRow key={participant.user_id}>
                  <Text>
                    {participant.label} · {t(`pages.dealDetail.manageFiles.files.contractSigning.contacts.${participant.role}`)}
                  </Text>
                  {participant.missing ? (
                    <TextField
                      type="email"
                      required
                      variant="standard"
                      value={emails[participant.user_id] || ''}
                      onChange={(event) => setEmails((current) => ({
                        ...current,
                        [participant.user_id]: event.target.value,
                      }))}
                      label={t('pages.dealDetail.manageFiles.files.contractSigning.contacts.emailLabel')}
                      autoComplete="email"
                      inputProps={{ maxLength: 255 }}
                    />
                  ) : (
                    <ExistingContact>{participant.masked_email}</ExistingContact>
                  )}
                </ContactRow>
              ))}
            </ContactList>

            <Actions>
              <Button type="button" variant="normal-invert" size="md" onClick={onClose} disabled={loading}>
                {t('buttons.cancel')}
              </Button>
              {status?.ready ? (
                <Button type="button" variant="primary" size="md" onClick={onReady} disabled={loading}>
                  {t('pages.dealDetail.manageFiles.files.contractSigning.contacts.continue')}
                </Button>
              ) : (
                <Button type="submit" variant="primary" size="md" disabled={loading || !status}>
                  {loading
                    ? t('pages.dealDetail.manageFiles.files.contractSigning.contacts.saving')
                    : t('pages.dealDetail.manageFiles.files.contractSigning.contacts.saveAndContinue')}
                </Button>
              )}
            </Actions>
          </form>
        </ScrollArea>
      </ModalSurface>
    </Modal>
  );
};

export default ESignContactModal;
